import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import icon from '../assets/icon.png';
import { LEGAL_DOCS, LegalDoc } from '../legal/legalContent';

/*
 * Renders the app's policies verbatim (see scripts/sync-legal.py). The only
 * thing added here is presentation: "- " lines become a real list, a short
 * "Label:" lead-in is bolded, and emails/URLs are clickable. No words change.
 */

const LINKABLE = /([\w.+-]+@[\w-]+\.[\w.-]*\w|https?:\/\/[^\s)]+[^\s).,])/g;
// "Prohibited Activities: You agree…" — a short label, then more text.
const LEAD_IN = /^([A-Z][^:.!?]{0,60}):(\s+\S[\s\S]*)$/;

const linkify = (text: string): React.ReactNode[] =>
  text.split(LINKABLE).map((part, i) => {
    if (i % 2 === 0) return part;
    const href = part.includes('@') && !part.startsWith('http') ? `mailto:${part}` : part;
    return (
      <a key={i} href={href} className="legal-link">
        {part}
      </a>
    );
  });

const renderLine = (line: string): React.ReactNode => {
  const m = LEAD_IN.exec(line);
  if (m && m[1].trim().split(/\s+/).length <= 6) {
    return (
      <>
        <strong>{m[1]}:</strong>
        {linkify(m[2])}
      </>
    );
  }
  return linkify(line);
};

const renderParagraph = (text: string, key: number) => {
  const out: React.ReactNode[] = [];
  // A blank line ("\n\n") starts a new block, e.g. the copyright agent address.
  text.split('\n\n').forEach((chunk, c) => {
    const lines = chunk.split('\n');
    let i = 0;
    while (i < lines.length) {
      if (lines[i].startsWith('- ')) {
        const items: string[] = [];
        while (i < lines.length && lines[i].startsWith('- ')) {
          items.push(lines[i].slice(2));
          i += 1;
        }
        out.push(
          <ul key={`${key}-${c}-${i}`} className="list">
            {items.map((item, j) => (
              <li key={j}>{renderLine(item)}</li>
            ))}
          </ul>
        );
      } else {
        const textLines: string[] = [];
        while (i < lines.length && !lines[i].startsWith('- ')) {
          textLines.push(lines[i]);
          i += 1;
        }
        out.push(
          <p key={`${key}-${c}-${i}`} className="paragraph">
            {textLines.map((l, j) => (
              <React.Fragment key={j}>
                {j > 0 && <br />}
                {renderLine(l)}
              </React.Fragment>
            ))}
          </p>
        );
      }
    }
  });
  return out;
};

const LegalPage: React.FC<{ doc: LegalDoc }> = ({ doc }) => {
  useEffect(() => {
    document.title = `${doc.title} | Rushmore Social`;
    window.scrollTo(0, 0);
  }, [doc]);

  const others = LEGAL_DOCS.filter((d) => d.slug !== doc.slug);

  return (
    <main className="page page-dark">
      <div className="page-inner policy">
        <Link to="/" className="policy-brand">
          <img src={icon} alt="" className="policy-brand-icon" />
          <span className="policy-brand-name">Rushmore Social</span>
        </Link>
        <h1 className="page-title">{doc.title}</h1>
        {doc.effectiveDate && (
          <p className="muted">
            <strong>Effective Date:</strong> {doc.effectiveDate}
          </p>
        )}

        {doc.blocks.map((block, i) =>
          block.type === 'heading' ? (
            <h2 key={i} className="section-title">
              {block.text}
            </h2>
          ) : (
            renderParagraph(block.text, i)
          )
        )}

        <nav className="legal-nav" aria-label="Other policies">
          <span className="legal-nav-label">More policies</span>
          {others.map((d) => (
            <Link key={d.slug} to={`/${d.slug}`} className="legal-nav-link">
              {d.title}
            </Link>
          ))}
        </nav>

        <p className="page-footer-note">
          © {new Date().getFullYear()} PTOA LLC. Rushmore is a product of PTOA LLC.
        </p>
      </div>
    </main>
  );
};

export default LegalPage;
